import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button } from '@mui/material';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { DirectoryLab } from './directory-browser-config';
import { getShortLanguageCode } from 'utils/format';
import { loadGoogleMaps } from 'utils/google-maps-loader';
import {
    createPositionDot,
    createSupplierMapPinLayer,
    SupplierMapPinLayer,
    SupplierMapPinSpec,
} from './supplier-map-pin';
import SupplierMapPlaceList from './supplier-map-place-list.component';

type SupplierMapProps = {
    /** Labs with a map position (the directory's onlyLocated page). */
    labs: DirectoryLab[];
    onSelect: (lab: DirectoryLab) => void;
    /** The note above the map, naming what is shown. */
    onlyLocatedKey: string;
};

// Fills most of the large dialog.
const MAP_HEIGHT = '70vh';
const SINGLE_LAB_ZOOM = 11;
// Sweden, until the labs are known.
const DEFAULT_CENTER = { lat: 62.0, lng: 15.0 };
const DEFAULT_ZOOM = 5;
const MY_POSITION_ZOOM = 11;
const POSITION_TIMEOUT_MS = 10000;
/** A position up to a minute old is good enough to centre the map. */
const POSITION_MAX_AGE_MS = 60000;
const canLocate = typeof navigator !== 'undefined' && 'geolocation' in navigator;

/**
 * DD-3674: listed labs as logo pins; clicking one opens its profile.
 * DD-3696: nearby labs cluster into count bubbles; "My position" centres the map on the
 * browser's location (with the browser's permission) and marks it with a dot.
 */
const SupplierMap: FC<SupplierMapProps> = ({ labs, onSelect, onlyLocatedKey }) => {
    const { t: translate } = useTranslation(['client-directory']);
    const containerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<google.maps.Map>();
    const pinLayerRef = useRef<SupplierMapPinLayer>();
    const onSelectRef = useRef(onSelect);
    onSelectRef.current = onSelect;
    const [isReady, setIsReady] = useState(false);
    const [hasFailed, setHasFailed] = useState(false);
    const positionDotRef = useRef<google.maps.OverlayView>();
    const [isLocating, setIsLocating] = useState(false);
    const [hasPositionFailed, setHasPositionFailed] = useState(false);
    // DD-3698: the labs of a bubble at one address, listed since zooming cannot split it.
    const [placeLabs, setPlaceLabs] = useState<DirectoryLab[]>([]);

    useEffect(() => {
        let isMounted = true;
        loadGoogleMaps(getShortLanguageCode(currentUserVar()?.localeFormat?.language))
            .then((maps) => {
                if (!isMounted || !containerRef.current) return;
                mapRef.current = new maps.Map(containerRef.current, {
                    center: DEFAULT_CENTER,
                    zoom: DEFAULT_ZOOM,
                    streetViewControl: false,
                    mapTypeControl: false,
                    fullscreenControl: false,
                    // In the dialog the wheel zooms the map directly (no Ctrl needed).
                    gestureHandling: 'greedy',
                    // DD-3698: fitBounds may land between levels. Whole levels only made a tall
                    // country (Sweden: 55–68°N) drop a level and open as Iceland to Kazakhstan.
                    isFractionalZoomEnabled: true,
                });
                setIsReady(true);
            })
            .catch(() => isMounted && setHasFailed(true));
        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        const map = mapRef.current;
        if (!isReady || !map) return;
        pinLayerRef.current?.setMap(null);
        const bounds = new google.maps.LatLngBounds();
        const pins: SupplierMapPinSpec[] = labs.flatMap((lab) => {
            if (!lab.geoLocation) return [];
            const position = { lat: lab.geoLocation.latitude, lng: lab.geoLocation.longitude };
            bounds.extend(position);
            return [{ lab, position, onClick: () => onSelectRef.current(lab) }];
        });
        pinLayerRef.current = createSupplierMapPinLayer(
            google.maps,
            pins,
            (count, isSamePlace) =>
                translate(
                    isSamePlace ? 'client-directory:MAP_SAME_PLACE_LABEL' : 'client-directory:MAP_CLUSTER_LABEL',
                    { count },
                ),
            setPlaceLabs,
        );
        pinLayerRef.current.setMap(map);
        if (pins.length === 1) {
            map.setCenter(bounds.getCenter());
            map.setZoom(SINGLE_LAB_ZOOM);
        } else if (pins.length > 1) {
            map.fitBounds(bounds, 48);
        }
    }, [isReady, labs]);

    const showMyPosition = () => {
        const map = mapRef.current;
        if (!map) return;
        setIsLocating(true);
        setHasPositionFailed(false);
        navigator.geolocation.getCurrentPosition(
            ({ coords }) => {
                const position = { lat: coords.latitude, lng: coords.longitude };
                positionDotRef.current?.setMap(null);
                positionDotRef.current = createPositionDot(google.maps, position);
                positionDotRef.current.setMap(map);
                map.setCenter(position);
                map.setZoom(MY_POSITION_ZOOM);
                setIsLocating(false);
            },
            // Denied, unavailable or timed out: the map stays where it is.
            () => {
                setHasPositionFailed(true);
                setIsLocating(false);
            },
            { timeout: POSITION_TIMEOUT_MS, maximumAge: POSITION_MAX_AGE_MS },
        );
    };

    return (
        <Box>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Alert severity="info" sx={{ flexGrow: 1 }} data-test="supplier-map.only-located">
                    {translate(onlyLocatedKey)}
                </Alert>
                {canLocate && !hasFailed ? (
                    <Button
                        startIcon={<MyLocationIcon />}
                        onClick={showMyPosition}
                        disabled={!isReady || isLocating}
                        sx={{ flexShrink: 0 }}
                        data-test="supplier-map.my-position-btn"
                    >
                        {translate('client-directory:MY_POSITION')}
                    </Button>
                ) : undefined}
            </Box>
            {hasPositionFailed ? (
                <Alert severity="warning" sx={{ mb: 2 }} data-test="supplier-map.position-failed">
                    {translate('client-directory:MY_POSITION_FAILED')}
                </Alert>
            ) : undefined}
            {hasFailed ? (
                <Alert severity="error" data-test="supplier-map.failed">
                    {translate('client-directory:SUPPLIER_MAP_LOAD_FAILED')}
                </Alert>
            ) : (
                <Box
                    ref={containerRef}
                    sx={{ height: MAP_HEIGHT, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                    data-test="supplier-map"
                />
            )}
            <SupplierMapPlaceList
                labs={placeLabs}
                onClose={() => setPlaceLabs([])}
                onSelect={(lab) => onSelectRef.current(lab)}
            />
        </Box>
    );
};

export default SupplierMap;
